import { useState } from 'react'
import { Camera, ExternalLink } from 'lucide-react'
import { ErrorState, FormDialog, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { isoDatePlusDays, todayLocal } from '@/lib/dates'
import { punchPhotoUrl, useGeofencePunches } from '../../api/geofence'
import { FACE_RESULTS, GEOFENCE_RESULTS, mapLink } from '../../types/geofence'
import type { FaceResult, GeofenceResult, PunchReviewView } from '../../types/geofence'
import { AuthImage } from './FaceTab'
import { formatMetres } from './geo'

const RESULT_FILTERS: GeofenceResult[] = ['FLAGGED', 'UNCHECKED', 'ACCEPTED', 'NOT_REQUIRED']
const FACE_FILTERS: FaceResult[] = ['MISMATCH', 'NO_FACE', 'NOT_REGISTERED', 'NOT_CHECKED', 'MATCHED']

const chip = 'rounded-full border border-[var(--color-border)] px-3 py-1 text-xs aria-pressed:border-[var(--color-primary)] aria-pressed:bg-[var(--color-primary-soft)] aria-pressed:text-[var(--color-primary)]'

/** Local-day boundaries as instants, so "today" means the viewer's today. */
function startOf(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString()
}

/**
 * App and kiosk punches with what the fence made of them. Refused punches never reach here:
 * a refusal is not saved, because a punch that did not happen is not attendance.
 */
export function PunchReviewTab() {
  const [from, setFrom] = useState(isoDatePlusDays(-7))
  const [to, setTo] = useState(todayLocal())
  const [results, setResults] = useState<GeofenceResult[]>(['FLAGGED', 'UNCHECKED'])
  const [faceResults, setFaceResults] = useState<FaceResult[]>(['MISMATCH', 'NO_FACE', 'NOT_REGISTERED'])
  const [photo, setPhoto] = useState<PunchReviewView | null>(null)
  const punches = useGeofencePunches({
    from: startOf(from),
    to: new Date(new Date(`${to}T00:00:00`).getTime() + 86_400_000).toISOString(),
    result: results,
    faceResult: faceResults,
  })

  const toggle = (result: GeofenceResult) =>
    setResults((previous) => previous.includes(result)
      ? previous.filter((r) => r !== result) : [...previous, result])
  const toggleFace = (result: FaceResult) =>
    setFaceResults((previous) => previous.includes(result)
      ? previous.filter((r) => r !== result) : [...previous, result])

  return (
    <div className="space-y-4">
      {photo && (
        <FormDialog open onOpenChange={(open) => { if (!open) setPhoto(null) }}
          title={`${photo.employeeName ?? photo.employeeCode ?? ''} — ${photo.direction === 'IN' ? 'punch in' : 'punch out'}`}
          description={`${new Date(photo.punchTime).toLocaleString()}. Opening this photo is recorded in the access log.`}
          submitLabel="Close" onSubmit={() => setPhoto(null)}>
          <AuthImage url={punchPhotoUrl(photo.id)} alt="Punch photo" className="mx-auto max-h-[60vh] rounded" />
          {photo.faceNote && <p className="text-sm text-[var(--color-body)]">{photo.faceNote}</p>}
        </FormDialog>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="pr-from">From</Label>
          <DatePicker id="pr-from" value={from} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-to">To</Label>
          <DatePicker id="pr-to" value={to} onChange={(event) => setTo(event.target.value)} />
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Location results to show">
          <span className="w-16 text-xs text-[var(--color-muted)]">Location</span>
          {RESULT_FILTERS.map((result) => (
            <button key={result} type="button" aria-pressed={results.includes(result)}
              onClick={() => toggle(result)} className={chip}>
              {GEOFENCE_RESULTS[result].label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Face results to show">
          <span className="w-16 text-xs text-[var(--color-muted)]">Face</span>
          {FACE_FILTERS.map((result) => (
            <button key={result} type="button" aria-pressed={faceResults.includes(result)}
              onClick={() => toggleFace(result)} className={chip}>
              {FACE_RESULTS[result].label}
            </button>
          ))}
        </div>
        <p className="text-xs text-[var(--color-muted)]">A punch shows when it matches any selected location or face result. Select none to see everything.</p>
      </div>

      {punches.isPending && <Skeleton className="h-48" />}
      {punches.isError && <ErrorState error={punches.error} onRetry={() => void punches.refetch()} />}
      {punches.data && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
                <th className="p-3 text-start font-medium">When</th>
                <th className="p-3 text-start font-medium">Employee</th>
                <th className="p-3 text-start font-medium">Location</th>
                <th className="p-3 text-start font-medium">Face</th>
                <th className="p-3 text-start font-medium">Site</th>
                <th className="p-3 text-end font-medium">Distance</th>
                <th className="p-3 text-start font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {punches.data.map((punch) => (
                <tr key={punch.id} className="border-b border-[var(--color-border)] align-top last:border-0">
                  <td className="whitespace-nowrap p-3">
                    <p>{new Date(punch.punchTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>
                    <p className="text-xs text-[var(--color-muted)]">{punch.direction === 'IN' ? 'Punch in' : 'Punch out'} · {punch.source.toLowerCase()}</p>
                  </td>
                  <td className="p-3">
                    <p className="text-[var(--color-heading)]">{punch.employeeName}</p>
                    <p className="text-xs text-[var(--color-muted)]">{punch.employeeCode}</p>
                  </td>
                  <td className="p-3">
                    <StatusBadge status={punch.result} tone={GEOFENCE_RESULTS[punch.result]?.tone ?? 'neutral'}
                      label={GEOFENCE_RESULTS[punch.result]?.label ?? punch.result} />
                  </td>
                  <td className="p-3">
                    {punch.faceResult
                      ? (
                        <StatusBadge status={punch.faceResult} tone={FACE_RESULTS[punch.faceResult]?.tone ?? 'neutral'}
                          label={FACE_RESULTS[punch.faceResult]?.label ?? punch.faceResult} />
                      )
                      : <span className="text-xs text-[var(--color-muted)]">—</span>}
                    {punch.faceScore != null && (
                      <p className="mt-1 text-xs tabular-nums text-[var(--color-muted)]">score {punch.faceScore.toFixed(2)}</p>
                    )}
                    {punch.photoAttached && (
                      <Button type="button" size="sm" variant="ghost" className="mt-1 h-7 px-2" onClick={() => setPhoto(punch)}>
                        <Camera className="size-3" aria-hidden="true" /> Photo
                      </Button>
                    )}
                  </td>
                  <td className="p-3">{punch.locationName ?? '—'}</td>
                  <td className="p-3 text-end tabular-nums">
                    {formatMetres(punch.distanceMetres)}
                    {punch.accuracyMetres != null && (
                      <p className="text-xs text-[var(--color-muted)]">± {punch.accuracyMetres} m</p>
                    )}
                  </td>
                  <td className="max-w-md p-3 text-xs text-[var(--color-body)]">
                    {punch.outsideReason && (
                      <p className="mb-1 text-sm text-[var(--color-heading)]">Reason: {punch.outsideReason}</p>
                    )}
                    <p>{punch.note}</p>
                    {punch.faceNote && punch.faceResult !== 'NOT_REQUIRED' && <p className="mt-1">{punch.faceNote}</p>}
                    {punch.latitude != null && punch.longitude != null && (
                      <a href={mapLink(punch.latitude, punch.longitude)} target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
                        Where it was made <ExternalLink className="size-3" aria-hidden="true" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {punches.data.length === 0 && (
            <p className="p-4 text-sm text-[var(--color-muted)]">No app or kiosk punches match in this period.</p>
          )}
        </Card>
      )}
    </div>
  )
}
